import { useEffect, useRef } from "react";
import { useMap } from "@vis.gl/react-google-maps";
import useEvent from "../../hooks/useEvent";

/**
 * Manages a Google Maps GroundOverlay for event custom map graphics.
 * Shows the overlay when the event has a mapOverlay configured and
 * the current zoom level is >= the configured minZoom.
 */
const EventMapOverlay = () => {
  const map = useMap();
  const { event } = useEvent();
  const overlayRef = useRef(null);

  useEffect(function manageGroundOverlay() {
    if (!map || !window.google?.maps) return;

    const overlay = event?.mapOverlay;
    if (!overlay?.imageUrl || !overlay?.bounds) {
      // No overlay configured — clean up if one exists
      if (overlayRef.current) {
        overlayRef.current.setMap(null);
        overlayRef.current = null;
      }
      return;
    }

    const { south, west, north, east } = overlay.bounds;
    if (south == null || west == null || north == null || east == null) return;

    const minZoom = overlay.minZoom ?? 15;
    const opacity = overlay.opacity ?? 0.75;

    // Create the GroundOverlay
    const bounds = new window.google.maps.LatLngBounds(
      new window.google.maps.LatLng(south, west),
      new window.google.maps.LatLng(north, east)
    );

    const groundOverlay = new window.google.maps.GroundOverlay(overlay.imageUrl, bounds, {
      opacity,
      clickable: false,
    });

    // Show/hide based on zoom
    const updateVisibility = () => {
      const currentZoom = map.getZoom();
      if (currentZoom >= minZoom) {
        if (!groundOverlay.getMap()) {
          groundOverlay.setMap(map);
        }
      } else {
        if (groundOverlay.getMap()) {
          groundOverlay.setMap(null);
        }
      }
    };

    updateVisibility();
    const zoomListener = map.addListener("zoom_changed", updateVisibility);

    // Clean up previous overlay
    if (overlayRef.current) {
      overlayRef.current.setMap(null);
    }
    overlayRef.current = groundOverlay;

    return () => {
      zoomListener.remove();
      groundOverlay.setMap(null);
      overlayRef.current = null;
    };
  }, [map, event]);

  return null;
};

export default EventMapOverlay;
